import { useRouter } from "@tanstack/react-router";
import { Download, Inbox, Upload } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { QueueDirection, RequestStatus } from "@/hooks/use-staffing-requests";
import { useStaffingRequests } from "@/hooks/use-staffing-requests";
import { RequestQueueRow } from "./RequestQueueRow";
import { UploadRequestDialog } from "./UploadRequestDialog";

/** Individual status chips. Error is here so failed uploads are never invisible. */
const STATUS_CHIPS: RequestStatus[] = [
  "New",
  "Mapping",
  "Mapped",
  "Committed",
  "Rejected",
  "Error",
];

/** Every status — the "All" view (includes Superseded versions of re-uploads). */
const EVERY_STATUS: RequestStatus[] = [...STATUS_CHIPS, "Superseded"];

const STATUS_LABELS: Record<RequestStatus, string> = {
  New: "New",
  Mapping: "Mapping",
  Mapped: "Mapped",
  Committed: "Committed",
  Rejected: "Rejected",
  Superseded: "Superseded",
  Error: "Error",
};

const PAGE_SIZE = 25;

type FilterMode = "actionable" | "all" | RequestStatus;

export function RequestQueue() {
  const router = useRouter();
  const [filter, setFilter] = useState<FilterMode>("actionable");
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Cursor pagination state. Reset to page 1 whenever the filter changes.
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [direction, setDirection] = useState<QueueDirection>("next");
  const [page, setPage] = useState(1);

  // "actionable" = server default (New + Mapping); "all" = every upload and request
  // regardless of status; a single chip narrows to that status.
  const statuses: string[] | undefined =
    filter === "actionable" ? undefined : filter === "all" ? EVERY_STATUS : [filter];

  const { data, isLoading, isFetching, isError, error, refetch } = useStaffingRequests({
    statuses,
    linked: unlinkedOnly ? "unlinked" : undefined,
    cursor,
    direction,
    limit: PAGE_SIZE,
  });

  function resetPaging() {
    setCursor(undefined);
    setDirection("next");
    setPage(1);
  }

  function selectFilter(next: FilterMode) {
    setFilter(next);
    resetPaging();
  }

  function toggleUnlinked() {
    setUnlinkedOnly((prev) => !prev);
    resetPaging();
  }

  function goNext() {
    if (!data?.nextCursor) return;
    setCursor(data.nextCursor);
    setDirection("next");
    setPage((p) => p + 1);
  }

  function goPrev() {
    if (!data?.prevCursor) return;
    setCursor(data.prevCursor);
    setDirection("prev");
    setPage((p) => Math.max(1, p - 1));
  }

  function handleRowClick(id: string) {
    router.navigate({ to: "/dashboard/staffing-requests/$id", params: { id } });
  }

  const rows = data?.data ?? [];

  return (
    <div className="px-6 py-5">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[20px] font-bold leading-tight">Staffing Requests</h1>
          <p className="text-[12px] text-muted-foreground">
            Inbound SOW and resource plan requests — map lines to Pods and commit.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" asChild>
            {/* Dropdowns are sourced from live Configuration at download time. */}
            <a href="/api/staffing-requests/template" download>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Download Template
            </a>
          </Button>
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <Upload className="mr-1.5 h-3.5 w-3.5" />
            Upload Request
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Status</span>
        <FilterChip
          label="Actionable"
          active={filter === "actionable"}
          onClick={() => selectFilter("actionable")}
        />
        <FilterChip label="All" active={filter === "all"} onClick={() => selectFilter("all")} />
        {STATUS_CHIPS.map((s) => (
          <FilterChip
            key={s}
            label={STATUS_LABELS[s]}
            active={filter === s}
            onClick={() => selectFilter(filter === s ? "actionable" : s)}
          />
        ))}
        <span className="ml-2 text-[11px] uppercase tracking-wide text-muted-foreground">Link</span>
        <FilterChip label="Unlinked only" active={unlinkedOnly} onClick={toggleUnlinked} />
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-md" />
          ))}
        </div>
      )}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load staffing requests."}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading &&
        !isError &&
        data !== undefined &&
        (rows.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No requests"
            description={
              unlinkedOnly
                ? "No unlinked requests — every request is linked to a Solution."
                : filter === "actionable"
                  ? "No actionable requests — all caught up."
                  : filter === "all"
                    ? "No uploads or requests yet."
                    : `No requests with status "${filter}".`
            }
            action={
              <Button size="sm" variant="outline" onClick={() => setUploadOpen(true)}>
                Upload Request
              </Button>
            }
          />
        ) : (
          <div className="space-y-4">
            <div className="overflow-hidden rounded-md border border-border bg-card">
              <table className="w-full text-[12px]">
                <thead className="bg-muted/40 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Source</th>
                    <th className="px-3 py-2 font-semibold">Account</th>
                    <th className="px-3 py-2 font-semibold">Solution</th>
                    <th className="px-3 py-2 font-semibold">Lines</th>
                    <th className="px-3 py-2 font-semibold">Intensity</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2 font-semibold">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <RequestQueueRow key={row.id} request={row} onClick={handleRowClick} />
                  ))}
                </tbody>
              </table>
            </div>

            <CursorPagination
              total={data.total}
              page={page}
              limit={PAGE_SIZE}
              hasNext={data.hasNext}
              hasPrev={data.hasPrev}
              onNext={goNext}
              onPrev={goPrev}
              loading={isFetching}
              itemLabel="requests"
            />
          </div>
        ))}

      <UploadRequestDialog open={uploadOpen} onOpenChange={setUploadOpen} />
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "inline-flex items-center rounded-full bg-foreground px-2.5 py-0.5 text-[11px] font-medium text-background transition"
          : "inline-flex items-center rounded-full border border-border bg-card px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground transition hover:border-foreground/40 hover:text-foreground"
      }
    >
      {label}
    </button>
  );
}
